// src/router/RestrictedAccess.jsx
//
// Rendered by ProtectedRoute in place of a permission-gated page's real
// content, instead of navigating to /unauthorized -- keeps the URL and the
// route mounted, so switching (in the topbar) to an account/project that
// does grant the permission just re-renders the real page on the next
// tick. A <Navigate> can't do that once it's already fired.
//
// Also reused directly (not via ProtectedRoute) by pages that only learn
// about an access gap from a live API response rather than upfront route
// config -- e.g. a studio page whose data fetch comes back 402/403 after
// the route already rendered. reason="subscription" covers that 402 case
// (the account isn't subscribed to the module at all, not fixable by a
// different role) with the same EmptyState shape and "go to Dashboard"
// action as the permission case, rather than a second near-duplicate
// component.

import PropTypes from "prop-types";
import { Lock, PackageX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { EmptyState, Button } from "../shared/ui";
import { useWorkspace } from "../context/WorkspaceContext";

export default function RestrictedAccess({ reason = "permission" }) {
  const navigate = useNavigate();
  const { currentAccount, currentEngagement } = useWorkspace();

  // Named account/project together, not one-or-the-other -- either switch
  // can be what put the current selection outside this page's granted
  // scope, and there's no reliable way from here to tell which one is
  // actually responsible (could be either, or both, or neither if the gap
  // is a plain org-wide permission). Showing the current context plainly
  // avoids asserting a specific cause that might be wrong.
  // Engagements use client_name, not name (see ProjectsPage.jsx's
  // "Project name" column) -- Account is the only one of the two that
  // actually has a .name field.
  const scopePath = [currentAccount?.name, currentEngagement?.client_name]
    .filter(Boolean)
    .join(" → ");

  const isSubscription = reason === "subscription";

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <EmptyState
        icon={isSubscription ? PackageX : Lock}
        title={
          isSubscription
            ? "This module isn't available for this account"
            : "You don't have permission to view this"
        }
        description={
          isSubscription
            ? currentAccount?.name
              ? `${currentAccount.name} doesn't have an active subscription for this module. Switch to a different account, or ask an admin to enable it.`
              : "The currently selected account doesn't have an active subscription for this module. Switch to a different account, or ask an admin to enable it."
            : scopePath
              ? `You don't have permission to view this resource under ${scopePath}. Switch to a different account or project, or go to the Dashboard.`
              : "You don't have permission to view this resource for the currently selected account or project."
        }
        action={
          <Button variant="secondary" onClick={() => navigate("/")}>
            Go to Dashboard
          </Button>
        }
      />
    </div>
  );
}

RestrictedAccess.propTypes = {
  // "permission" (default): a role/permission gap -- ProtectedRoute's own
  // usage, and a live 403 from an API call.
  // "subscription": the account has no active module subscription at all
  // -- a live 402 from an API call. Never set by ProtectedRoute itself,
  // since route-level gating only checks permissions, not subscriptions.
  reason: PropTypes.oneOf(["permission", "subscription"]),
};
